import { useEffect, useState, type ReactElement } from "react";
import { cn } from "@/lib/cn";

export type ToastKind = "success" | "error";
export interface ToastItem {
  id: number;
  kind: ToastKind;
  message: string;
}

interface Props {
  toasts: ToastItem[];
  onDismiss: (id: number) => void;
}

const KIND_STYLES: Record<ToastKind, string> = {
  success: "bg-emerald-600 text-white",
  error: "bg-red-600 text-white",
};

export function ToastContainer({ toasts, onDismiss }: Readonly<Props>): ReactElement {
  return (
    <div aria-live="polite" className="fixed bottom-4 right-4 z-50 flex flex-col gap-2">
      {toasts.map((t) => (
        <ToastView key={t.id} toast={t} onDismiss={onDismiss} />
      ))}
    </div>
  );
}

function ToastView({
  toast,
  onDismiss,
}: Readonly<{
  toast: ToastItem;
  onDismiss: (id: number) => void;
}>): ReactElement {
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setLeaving(true);
      setTimeout(() => {
        onDismiss(toast.id);
      }, 200);
    }, 4_000);
    return () => {
      clearTimeout(timer);
    };
  }, [toast.id, onDismiss]);

  return (
    <div
      role="status"
      className={cn(
        "rounded px-4 py-2 shadow-lg transition-opacity",
        KIND_STYLES[toast.kind],
        leaving && "opacity-0",
      )}
    >
      {toast.message}
    </div>
  );
}
